/**
 * AccessibilityStatement — public page at `/accessibility`.
 *
 * Implements the model accessibility statement required by:
 *   • Commission Implementing Decision (EU) 2018/1523 of 11 October 2018
 *   • Web Accessibility Directive (EU) 2016/2102
 *   • Greek Law 4727/2020 (Digital Governance Code, Article 38)
 *   • European Accessibility Act — Directive (EU) 2019/882 (in force since 28 Jun 2025)
 *
 * Sections (per the official template):
 *   1. Introduction / scope
 *   2. Compliance status (WCAG 2.1 AA + EN 301 549 v3.2.1)
 *   3. Non-accessible content
 *   4. Preparation of the statement (method + dates)
 *   5. Feedback and contact information
 *   6. Enforcement procedure (ΕΣΑμεΑ + Synigoros tou Politi)
 *
 * The page is publicly reachable without authentication so auditors can
 * verify the statement before logging in.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  CheckCircle2, Mail, ExternalLink, FileText, Calendar,
  ShieldCheck, AlertTriangle, ArrowLeft, Accessibility, Download,
} from 'lucide-react';
import { BACKEND_URL } from '../config';
import ReportAccessibilityIssueForm from './ReportAccessibilityIssueForm';

// Contact + enforcement bodies (Greek public-sector defaults).
const CONTACT_EMAIL = 'accessibility@isisp.eu';
const FEEDBACK_DEADLINE_DAYS = 15; // statutory under Greek Law 4727/2020
const LAST_REVIEW_DATE_ISO = '2026-06-12';
const LAST_REVIEW_DATE_DISPLAY_EN = '12 June 2026';
const LAST_REVIEW_DATE_DISPLAY_EL = '12 Ιουνίου 2026';

const ENFORCEMENT_BODIES = [
  {
    name_en: 'Greek Ombudsman (Synigoros tou Politi)',
    name_el: 'Συνήγορος του Πολίτη',
    url: 'https://www.synigoros.gr/',
    role_en: 'Independent constitutional authority for the enforcement of the Web Accessibility Directive in Greece.',
    role_el: 'Ανεξάρτητη συνταγματική αρχή για την επιβολή της Οδηγίας Προσβασιμότητας στην Ελλάδα.',
  },
  {
    name_en: 'National Confederation of Disabled People (ΕΣΑμεΑ)',
    name_el: 'Εθνική Συνομοσπονδία Ατόμων με Αναπηρία (ΕΣΑμεΑ)',
    url: 'https://www.esamea.gr/',
    role_en: 'National disability federation — accessibility advocacy and complaints support.',
    role_el: 'Εθνική συνομοσπονδία αναπηρίας — υποστήριξη παραπόνων προσβασιμότητας.',
  },
  {
    name_en: 'Ministry of Digital Governance (MinDigital)',
    name_el: 'Υπουργείο Ψηφιακής Διακυβέρνησης',
    url: 'https://mindigital.gr/',
    role_en: 'Competent authority for monitoring compliance with Greek Law 4727/2020.',
    role_el: 'Αρμόδιος φορέας παρακολούθησης του Ν. 4727/2020.',
  },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <section aria-labelledby={`${id}-heading`} className="space-y-3">
    <h2 id={`${id}-heading`} className="text-xl font-semibold text-slate-900 flex items-center gap-2">
      {Icon && <Icon className="h-5 w-5 text-blue-700" aria-hidden="true" />}
      {title}
    </h2>
    <div className="text-sm leading-relaxed text-slate-700 space-y-3">
      {children}
    </div>
  </section>
);

const StatementContent = ({ lang }) => {
  const isEL = lang === 'el';
  const fmtList = (en, el) => (isEL ? el : en);

  return (
    <article aria-labelledby="statement-title" lang={lang}>
      <header className="mb-8">
        <h1 id="statement-title" className="text-3xl font-bold text-slate-900 mb-2">
          {isEL ? 'Δήλωση Προσβασιμότητας' : 'Accessibility Statement'}
        </h1>
        <p className="text-sm text-slate-600">
          {isEL
            ? 'Πλατφόρμα ISISP — Ολοκληρωμένο Σύστημα Κοινωνικών Επενδύσεων'
            : 'ISISP Platform — Integrated Social Investment System'}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {isEL ? 'Τελευταία αναθεώρηση: ' : 'Last reviewed: '}
          <time dateTime={LAST_REVIEW_DATE_ISO}>
            {isEL ? LAST_REVIEW_DATE_DISPLAY_EL : LAST_REVIEW_DATE_DISPLAY_EN}
          </time>
        </p>
      </header>

      <div className="space-y-8">

        {/* 1. Introduction */}
        <Section id="intro" icon={Accessibility} title={isEL ? '1. Εισαγωγή' : '1. Introduction'}>
          <p>
            {fmtList(
              'The ISISP Platform (Integrated Social Investment System) is committed to making its services accessible in accordance with Greek Law 4727/2020 transposing Web Accessibility Directive (EU) 2016/2102 and the European Accessibility Act — Directive (EU) 2019/882.',
              'Η Πλατφόρμα ISISP (Ολοκληρωμένο Σύστημα Κοινωνικών Επενδύσεων) δεσμεύεται να καθιστά τις υπηρεσίες της προσβάσιμες σύμφωνα με τον Ν. 4727/2020, που ενσωματώνει την Οδηγία (ΕΕ) 2016/2102 για την προσβασιμότητα ιστοτόπων, και την Ευρωπαϊκή Πράξη Προσβασιμότητας — Οδηγία (ΕΕ) 2019/882.',
            )}
          </p>
          <p>
            {fmtList(
              'This statement applies to the website published at https://isisp.eu and all of its sub-pages, including authenticated areas accessed by social actors, social investors, project managers and administrators.',
              'Η παρούσα δήλωση αφορά τον ιστότοπο που δημοσιεύεται στη διεύθυνση https://isisp.eu και όλες τις υποσελίδες του, συμπεριλαμβανομένων των αυθεντικοποιημένων περιοχών που χρησιμοποιούν κοινωνικοί φορείς, κοινωνικοί επενδυτές, διαχειριστές έργων και διαχειριστές συστήματος.',
            )}
          </p>
        </Section>

        {/* 2. Compliance status */}
        <Section id="status" icon={CheckCircle2} title={isEL ? '2. Κατάσταση Συμμόρφωσης' : '2. Compliance status'}>
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
            <p className="font-semibold text-emerald-900">
              {fmtList(
                'This website is in full compliance with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.',
                'Αυτός ο ιστότοπος είναι σε πλήρη συμμόρφωση με τις Κατευθυντήριες Οδηγίες Προσβασιμότητας Περιεχομένου Ιστού (WCAG) 2.1 Επίπεδο AA.',
              )}
            </p>
            <p className="text-emerald-800 text-xs mt-2">
              {fmtList(
                'Mapped to the EU harmonised standard EN 301 549 v3.2.1.',
                'Αντιστοιχίζεται με το ευρωπαϊκό εναρμονισμένο πρότυπο EN 301 549 v3.2.1.',
              )}
            </p>
          </div>
          <p>
            {fmtList(
              'A WCAG 2.1 AA conformance test conducted on 12 June 2026 with axe-core v4.10.2 against 11 representative pages (Landing, Login, Dashboard, Project Details, Analytics & AI, User Guides & Documentation, Social Actor Registry, Social Investor Registry, Combined Investments, Projects List, Needs & Resources Map) returned 0 violation instances.',
              'Έλεγχος συμμόρφωσης WCAG 2.1 AA που πραγματοποιήθηκε στις 12 Ιουνίου 2026 με το axe-core v4.10.2 σε 11 αντιπροσωπευτικές σελίδες (Αρχική, Σύνδεση, Πίνακας Ελέγχου, Λεπτομέρειες Έργου, Αναλύσεις & AI, Οδηγοί Χρήσης & Τεκμηρίωση, Μητρώο Κοινωνικών Φορέων, Μητρώο Κοινωνικών Επενδυτών, Μικτές Επενδύσεις, Λίστα Έργων, Χάρτης Αναγκών & Πόρων) επέστρεψε 0 παραβιάσεις.',
            )}
          </p>
        </Section>

        {/* 3. Non-accessible content */}
        <Section id="non-accessible" icon={AlertTriangle} title={isEL ? '3. Μη Προσβάσιμο Περιεχόμενο' : '3. Non-accessible content'}>
          <p>
            {fmtList(
              'At the time of preparation of this statement, no fully non-accessible content has been identified within the scope of WCAG 2.1 Level AA. The following items are however known limitations:',
              'Κατά την προετοιμασία της παρούσας δήλωσης, δεν εντοπίστηκε πλήρως μη προσβάσιμο περιεχόμενο εντός του πεδίου εφαρμογής του WCAG 2.1 Επίπεδο AA. Ωστόσο, τα ακόλουθα είναι γνωστοί περιορισμοί:',
            )}
          </p>
          <ul className="list-disc list-inside space-y-1 pl-4">
            <li>
              {fmtList(
                'User-generated content (project descriptions, KPI text, uploaded files): the platform cannot guarantee that user-supplied images contain meaningful alternative text or that uploaded PDFs are tagged. Administrators are advised to add alternative text and use tagged PDFs.',
                'Περιεχόμενο από τους χρήστες (περιγραφές έργων, κείμενα KPI, μεταφορτωμένα αρχεία): η πλατφόρμα δεν μπορεί να εγγυηθεί ότι οι εικόνες των χρηστών περιλαμβάνουν ουσιαστικό εναλλακτικό κείμενο ή ότι τα μεταφορτωμένα PDF είναι σωστά δομημένα.',
              )}
            </li>
            <li>
              {fmtList(
                'Third-party map tiles (OpenStreetMap): the underlying tile imagery is not described textually. A complete list of map points is provided in tabular form below each map as a text alternative.',
                'Πλακίδια χάρτη τρίτων (OpenStreetMap): η υποκείμενη εικόνα δεν περιγράφεται κειμενικά. Παρέχεται πλήρης λίστα των σημείων του χάρτη σε μορφή πίνακα κάτω από κάθε χάρτη ως εναλλακτικό κείμενο.',
              )}
            </li>
          </ul>
        </Section>

        {/* 4. Preparation */}
        <Section id="preparation" icon={FileText} title={isEL ? '4. Προετοιμασία της Δήλωσης' : '4. Preparation of this statement'}>
          <ul className="list-disc list-inside space-y-1 pl-4">
            <li>
              <strong>{isEL ? 'Μέθοδος αξιολόγησης: ' : 'Method used: '}</strong>
              {fmtList(
                'Self-evaluation combining automated testing (axe-core v4.10.2, the same engine used by WAVE, Pa11y and Lighthouse) and manual code review against the WCAG 2.1 A + AA success criteria.',
                'Αυτο-αξιολόγηση που συνδυάζει αυτόματο έλεγχο (axe-core v4.10.2, η ίδια μηχανή με WAVE, Pa11y και Lighthouse) και χειροκίνητη επιθεώρηση κώδικα έναντι των κριτηρίων WCAG 2.1 A + AA.',
              )}
            </li>
            <li>
              <strong>{isEL ? 'Ημερομηνία αξιολόγησης: ' : 'Date of assessment: '}</strong>
              <time dateTime={LAST_REVIEW_DATE_ISO}>
                {isEL ? LAST_REVIEW_DATE_DISPLAY_EL : LAST_REVIEW_DATE_DISPLAY_EN}
              </time>
            </li>
            <li>
              <strong>{isEL ? 'Ημερομηνία τελευταίας αναθεώρησης: ' : 'Last review date: '}</strong>
              <time dateTime={LAST_REVIEW_DATE_ISO}>
                {isEL ? LAST_REVIEW_DATE_DISPLAY_EL : LAST_REVIEW_DATE_DISPLAY_EN}
              </time>
            </li>
            <li>
              <strong>{isEL ? 'Ημερομηνία σύνταξης της παρούσας δήλωσης: ' : 'Date this statement was prepared: '}</strong>
              <time dateTime={LAST_REVIEW_DATE_ISO}>
                {isEL ? LAST_REVIEW_DATE_DISPLAY_EL : LAST_REVIEW_DATE_DISPLAY_EN}
              </time>
            </li>
          </ul>
        </Section>

        {/* 5. Feedback */}
        <Section id="feedback" icon={Mail} title={isEL ? '5. Αναφορά Προβλημάτων Προσβασιμότητας' : '5. Feedback and contact information'}>
          <p>
            {fmtList(
              'If you encounter an accessibility issue on this website, please notify us so we can address it. We aim to respond within',
              'Εάν συναντήσετε πρόβλημα προσβασιμότητας σε αυτόν τον ιστότοπο, παρακαλούμε να μας ειδοποιήσετε ώστε να το αντιμετωπίσουμε. Στοχεύουμε να απαντήσουμε εντός',
            )}
            {' '}
            <strong>{FEEDBACK_DEADLINE_DAYS} {isEL ? 'ημερών' : 'days'}</strong>
            {fmtList(
              ' as required by Greek Law 4727/2020.',
              ' όπως απαιτείται από τον Ν. 4727/2020.',
            )}
          </p>
          <div className="rounded-md bg-blue-50 border border-blue-200 p-4">
            <p className="font-semibold text-blue-900 mb-2">
              {isEL ? 'Επικοινωνία / Contact:' : 'Contact:'}
            </p>
            <ul className="space-y-1 text-blue-900">
              <li>
                <Mail className="inline h-4 w-4 mr-1" aria-hidden="true" />
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Accessibility issue on isisp.eu')}`}
                  className="underline hover:no-underline"
                  data-testid="accessibility-contact-email"
                >
                  {CONTACT_EMAIL}
                </a>
              </li>
            </ul>
            <div className="mt-3">
              <ReportAccessibilityIssueForm lang={lang} />
            </div>
            <p className="text-xs text-blue-900 mt-2">
              {isEL
                ? 'Δεν χρειάζεται να ανοίξετε e-mail πελάτη — χρησιμοποιήστε τη φόρμα παραπάνω εάν προτιμάτε.'
                : 'No need to open an email client — use the form above if you prefer.'}
            </p>
          </div>
          <p className="text-xs text-slate-500">
            {fmtList(
              'Please include the page URL, a description of the issue, the browser and assistive technology you are using, and (where possible) a screenshot.',
              'Παρακαλούμε συμπεριλάβετε τη διεύθυνση URL της σελίδας, περιγραφή του προβλήματος, τον φυλλομετρητή και την υποστηρικτική τεχνολογία που χρησιμοποιείτε και (αν είναι δυνατόν) στιγμιότυπο οθόνης.',
            )}
          </p>
        </Section>

        {/* 6. Enforcement */}
        <Section id="enforcement" icon={ShieldCheck} title={isEL ? '6. Διαδικασία Επιβολής' : '6. Enforcement procedure'}>
          <p>
            {fmtList(
              'If you are not satisfied with our response to your accessibility complaint within the statutory deadline, you may contact the following bodies:',
              'Εάν δεν είστε ικανοποιημένοι με την απάντησή μας στην καταγγελία προσβασιμότητας εντός της θεσμικής προθεσμίας, μπορείτε να απευθυνθείτε στους ακόλουθους φορείς:',
            )}
          </p>
          <ul className="space-y-3">
            {ENFORCEMENT_BODIES.map((body) => (
              <li
                key={body.url}
                className="border border-slate-200 rounded-md p-3 bg-white"
              >
                <a
                  href={body.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                  data-testid={`enforcement-link-${body.url}`}
                >
                  {isEL ? body.name_el : body.name_en}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
                <p className="text-xs text-slate-600 mt-1">
                  {isEL ? body.role_el : body.role_en}
                </p>
              </li>
            ))}
          </ul>
        </Section>

        {/* Footer */}
        <footer className="pt-6 border-t border-slate-200 text-xs text-slate-500">
          <p>
            {fmtList(
              'This accessibility statement follows the model defined in Commission Implementing Decision (EU) 2018/1523.',
              'Η παρούσα δήλωση προσβασιμότητας ακολουθεί το πρότυπο που ορίζεται στην Εκτελεστική Απόφαση (ΕΕ) 2018/1523 της Επιτροπής.',
            )}
          </p>
          <p className="mt-1">
            {fmtList(
              'Funded by the European Union — NextGenerationEU / Greece 2.0 National Recovery and Resilience Plan.',
              'Χρηματοδοτείται από την Ευρωπαϊκή Ένωση — NextGenerationEU / Ελλάδα 2.0 Εθνικό Σχέδιο Ανάκαμψης & Ανθεκτικότητας.',
            )}
          </p>
        </footer>
      </div>
    </article>
  );
};

export default function AccessibilityStatement() {
  const { i18n } = useTranslation();
  // Mirror the global language switcher; default to EN if i18n isn't initialised.
  const [lang, setLang] = useState(
    (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2) === 'el' ? 'el' : 'en'
  );

  // Keep <html lang> in sync as the user toggles languages on this page.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Top bar with language toggle + back link */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            data-testid="accessibility-back-home"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {lang === 'el' ? 'Επιστροφή στην Αρχική' : 'Back to Home'}
          </Link>
          <div className="flex items-center gap-2">
            <a
              href={`${BACKEND_URL}/api/accessibility/vpat.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              data-testid="accessibility-vpat-download"
              aria-label={lang === 'el'
                ? 'Λήψη Έκθεσης Συμμόρφωσης Προσβασιμότητας (VPAT 2.4 EU, PDF)'
                : 'Download Accessibility Conformance Report (VPAT 2.4 EU, PDF)'}
              title={lang === 'el'
                ? 'VPAT 2.4 EU — Έκθεση Συμμόρφωσης Προσβασιμότητας (PDF)'
                : 'VPAT 2.4 EU — Accessibility Conformance Report (PDF)'}
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              {lang === 'el' ? 'VPAT (PDF)' : 'VPAT (PDF)'}
            </a>
            <div
              className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md px-1 py-0.5"
              role="group"
              aria-label={lang === 'el' ? 'Επιλογή γλώσσας' : 'Language selector'}
            >
              <Button
                size="sm"
                variant={lang === 'en' ? 'default' : 'ghost'}
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
                className="h-7 px-2 text-xs"
                data-testid="accessibility-lang-en"
              >
                EN
              </Button>
              <Button
                size="sm"
                variant={lang === 'el' ? 'default' : 'ghost'}
                onClick={() => setLang('el')}
                aria-pressed={lang === 'el'}
                className="h-7 px-2 text-xs"
                data-testid="accessibility-lang-el"
              >
                EL
              </Button>
            </div>
          </div>
        </div>

        <Card className="bg-white shadow-sm" data-testid="accessibility-statement-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Accessibility className="h-6 w-6 text-blue-700" aria-hidden="true" />
              {lang === 'el' ? 'Δήλωση Προσβασιμότητας' : 'Accessibility Statement'}
            </CardTitle>
          </CardHeader>
          <CardContent className="py-6">
            <StatementContent lang={lang} />
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          <Calendar className="inline h-3 w-3 mr-1" aria-hidden="true" />
          {lang === 'el' ? 'Έκδοση: ' : 'Version: '}
          <time dateTime={LAST_REVIEW_DATE_ISO}>
            {lang === 'el' ? LAST_REVIEW_DATE_DISPLAY_EL : LAST_REVIEW_DATE_DISPLAY_EN}
          </time>
        </p>
      </div>
    </div>
  );
}
